import React, { lazy, Suspense, useState } from "react";
import { BrowserRouter, Route, Switch, useHistory, useParams, Redirect } from "react-router-dom";
import Auth0ProviderWithNavigate from "./auth0-provider-with-navigate";
import HelmetComponent from "./components/common/HelmetComponent";
import { ErrorBoundary } from "react-error-boundary";
import FullPageErrorFallback from "./components/common/FullPageErrorFallback";
import FullPageSpinner from "./components/common/FullPageSpinner";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { positions, Provider as AlertProvider } from "react-alert";
import TagManager from "react-gtm-module";
import { tagManagerArgs } from "./config";
import AlertTemplate from "./components/AlertTemplate";
import PrivacyPolicyView from "./pages/PrivacyPolicy";
import AutobizTranslateProvider from "./AutobizTranslateProvider";
import Didomi from "./components/common/Didomi";
import "react-toastify/dist/ReactToastify.css";
import { AutobizTheme, defaultTheme } from "autobiz-strap";
import Announcement from "./pages/Announcement";

const LoginView = lazy(() => import("./pages/Login"));
const Records = lazy(() => import("./pages/Records"));
const RecordsDetails = lazy(() => import("./pages/Announcement"));
const AuthRequiredRoute = lazy(() => import("./components/Login/AuthRequiredRoute"));
const Dealers = lazy(() => import("./pages/Dealers"));
const DealerDetails = lazy(() => import("./pages/DealerDetails"));
const Sessions = lazy(() => import("./pages/Sessions"));
const Register = lazy(() => import("./pages/Register"));
const Reports = lazy(() => import("./pages/Reports"));
const Claims = lazy(() => import("./pages/Claims"));
const PdfViewer = lazy(() => import("./pages/PdfViewer"));
const Search = lazy(() => import("./pages/Search"));
const WatchList = lazy(() => import("./pages/WatchList"));
const MyAccount = lazy(() => import("./pages/MyAccount"));
const Offers = lazy(() => import("./pages/Offers"));

const stage = process.env.REACT_APP_ENV === "prod" ? "prod" : "dev";

function App() {
  const history = useHistory();
  TagManager.initialize(tagManagerArgs);
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 0,
        // staleTime: 0,
      },
    },
  });

  return (
    <AutobizTheme theme={defaultTheme}>
      <Auth0ProviderWithNavigate>
        <AutobizTranslateProvider>
          <AlertProvider template={AlertTemplate} position={positions.BOTTOM_RIGHT} timeout={5000}>
            <HelmetProvider>
              <HelmetComponent />
              <QueryClientProvider client={queryClient}>
                <ErrorBoundary
                  FallbackComponent={FullPageErrorFallback}
                  onReset={() => history.push("/")}
                >
                  <Suspense>
                    <Switch>
                      {/*<AuthRequiredRoute exact path="/dealers" component={Dealers} />*/}
                      <AuthRequiredRoute exact path="/sessions" component={Sessions} />
                      {/*<AuthRequiredRoute exact path="/dealers/:refId" component={DealerDetails} />*/}
                      {/*<AuthRequiredRoute exact path="/records" component={Records} />*/}
                      <AuthRequiredRoute
                        exact
                        path="/records/:refId"
                        component={RecordsDetails}
                        withFooter={false}
                      />
                      <AuthRequiredRoute
                        exact
                        path="/pdfViewer/:refId"
                        component={PdfViewer}
                        withFooter={false}
                      />
                      <AuthRequiredRoute
                        exact
                        path="/records/:refId/:offerType/:offerId"
                        component={RecordsDetails}
                        withFooter={false}
                      />
                      <AuthRequiredRoute
                        exact
                        path="/claim/:refId"
                        component={Claims}
                        withFooter={false}
                      />
                      <AuthRequiredRoute
                        exact
                        path="/reports/:refId"
                        component={Reports}
                        withFooter={false}
                      />
                      <Route exact path="/" component={LoginView} />
                      <Route exact path="/register" component={Register} />
                      <Route
                        exact
                        path="/politique-de-protection-des-donnees"
                        component={PrivacyPolicyView}
                      />
                      <Route
                        exact
                        path="/politica-sulla-riservatezza"
                        component={PrivacyPolicyView}
                      />
                      <Route exact path="/datenschutzrichtlinie" component={PrivacyPolicyView} />
                      <Route exact path="/privacy-policy" component={PrivacyPolicyView} />
                      <Route
                        exact
                        path="/politica-de-confidencialidad"
                        component={PrivacyPolicyView}
                      />
                      <Route exact path="/politica-de-privacidade" component={PrivacyPolicyView} />
                      <AuthRequiredRoute exact path="/search" component={Search} />
                      <AuthRequiredRoute exact path="/search/:refId" component={Search} />
                      <AuthRequiredRoute exact path="/watchlist" component={WatchList} />
                      <AuthRequiredRoute exact path="/myAccount" component={MyAccount} />
                      <AuthRequiredRoute exact path="/offers" component={Offers} />
                      <Route
                        exact
                        path="/offers"
                        render={() => <Redirect to="/offers/onGoingOffer" />}
                      />
                      {/* Handle tab-based routes */}
                      <AuthRequiredRoute exact path="/offers/:tab" component={Offers} />
                      <Redirect from="*" to="/search" />
                    </Switch>
                  </Suspense>
                </ErrorBoundary>
                {process.env.NODE_ENV === "development" && (
                  <ReactQueryDevtools initialIsOpen={false} />
                )}
              </QueryClientProvider>
              <Didomi />
            </HelmetProvider>
          </AlertProvider>
        </AutobizTranslateProvider>
      </Auth0ProviderWithNavigate>
    </AutobizTheme>
  );
}

export default () => {
  return (
    <BrowserRouter>
      <App />
    </BrowserRouter>
  );
};
